import moment from 'moment';
import constant from '../../app.constant';
import {
    CONTROLX_SEVERITIES,
    CONTROLX_TZ_OFFSET,
    ControlxActivity,
    ControlxSeverity,
    ControlxWatching
} from './controlx-activity.model';

/**
 * Turns raw ControlX entries into the flat strings the Activity rail renders.
 *
 * Kept out of both the service (which only speaks HTTP/SSE) and the component
 * (which only holds state) so any other page can reuse the same presentation
 * without re-deriving it — the rail on Order Detail is the first consumer, not
 * the only intended one.
 */

/**
 * Card accent, from `severity`. Drives the left bar and the timeline node only —
 * never the badge, which follows the feed prefix instead.
 */
export type ControlxTone = 'critical' | 'danger' | 'warning' | 'success' | 'info';

/** Badge palette, from `prefix`. */
export type ControlxPrefixTone =
    'agent' | 'sla' | 'escalation' | 'comms' | 'risk' | 'order' | 'default';

/**
 * Which filter tab a row falls under.
 *
 * `decision` and `action` partition the feed — every row is exactly one — so
 * their counts add up to the total. `contact` is a cross-cutting flag on top
 * (a row that reached a human is still an action), which is why it is a
 * separate boolean rather than a third member here.
 */
export type ControlxGroup = 'decision' | 'action';

/** Which glyph the timeline node shows. */
export type ControlxIconKey =
    'alert' | 'clock' | 'comms' | 'ticket' | 'chip' | 'package' | 'brain' | 'tool' | 'shield';

/** What a feed row needs, already resolved — the template does no work. */
export interface ControlxActivityView {
    /** `db_<activity_id>`; also the @for track key. */
    id: string;
    severity: ControlxSeverity;
    /** Primary line. Never blank — falls back to the category, then a generic label. */
    message: string;
    /** Secondary line, flattened from `detail`. Empty when there is nothing to say. */
    detailText: string;
    /** Short time label in IST. */
    timeLabel: string;
    /** Full IST timestamp, for the time element's tooltip. */
    timeTitle: string;
    /** Agent / prefix / source / rider / trip / audit ids, for the row tooltip. */
    contextTitle: string;
    category: string;
    source: string;

    // --- presentation, all derived from fields the API already sends ---
    /** `prefix`, uppercased — the lead badge (SUPERVISOR, SLA, ESCALATION…). */
    prefixLabel: string;
    prefixTone: ControlxPrefixTone;
    tone: ControlxTone;
    /** Human wording for the accent, so severity is never colour-only. */
    toneLabel: string;
    /** `source` uppercased, carried only on escalation cards (see below). */
    sourceLabel: string;
    /** `agent_name` cleaned into a readable role, e.g. `react_supervisor` → Supervisor. */
    agentDisplayName: string;
    /** The raw `agent_name`, shown under the display name. */
    agentRaw: string;
    /** Two letters for the identity chip. */
    agentInitials: string;
    /** `detail.confidence` as a whole percent, or null when absent. */
    confidence: number | null;
    /** `stroke-dasharray` for the confidence ring. */
    confidenceDash: string;
    /** `detail.trigger_type` uppercased, e.g. SUPERVISOR_DECISION. */
    statusLabel: string;
    iconKey: ControlxIconKey;
    /** Which filter tab owns this row — `decision` or `action`, never both. */
    group: ControlxGroup;
    /** True when the row records ControlX reaching a human or a partner desk. */
    isContact: boolean;
    /**
     * True for the few seconds after a row lands on the live stream, so the UI
     * can announce it as an arrival. Never set from the REST snapshot: those
     * rows already existed, and flashing thirty of them on page load would say
     * "thirty things just happened".
     */
    isNew?: boolean;

    /** The untouched entry, for anything a future consumer needs. */
    entry: ControlxActivity;
}

/**
 * Last-resort key for a row carrying neither `id` nor `activity_id`.
 *
 * Derived from the row's own content, so the same logical entry arriving twice
 * — once in the REST snapshot, once as a stream frame — still collapses to a
 * single row. Exists because the spec warns that "payload keys can vary
 * slightly by producer": a feed whose producer does not stamp a DB id would
 * otherwise vanish entirely, and silently, since a row with no key is skipped.
 */
function syntheticControlxId(entry: ControlxActivity): string {
    const parts = [
        entry.timestamp,
        entry.prefix,
        entry.category,
        entry.agent_name,
        typeof entry.message === 'string' ? entry.message.slice(0, 120) : ''
    ].map(part => (part === null || part === undefined ? '' : String(part).trim()));

    // An entry with nothing in any of these has nothing to render either.
    return parts.some(Boolean) ? `syn_${parts.join('|')}` : '';
}

/** The dedupe key: the API's `id`, then the DB id, then a content-derived one. */
export function controlxEntryId(entry: ControlxActivity | null | undefined): string {
    if (!entry) return '';
    if (typeof entry.id === 'string' && entry.id.trim()) return entry.id.trim();
    if (entry.activity_id !== null && entry.activity_id !== undefined) return `db_${entry.activity_id}`;
    return syntheticControlxId(entry);
}

/** Newest first, matching the REST snapshot's own order. */
export function compareControlxEntriesDesc(a: ControlxActivity, b: ControlxActivity): number {
    const at = (a?.timestamp || '') as string;
    const bt = (b?.timestamp || '') as string;
    // IST-naive ISO strings sort correctly as plain strings, so no parsing here.
    if (at !== bt) return at < bt ? 1 : -1;
    const aid = Number(a?.activity_id ?? 0);
    const bid = Number(b?.activity_id ?? 0);
    return bid - aid;
}

function normaliseSeverity(value: unknown): ControlxSeverity {
    const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return (CONTROLX_SEVERITIES as readonly string[]).includes(raw)
        ? (raw as ControlxSeverity)
        : 'info';
}

/** `agent_decision` becomes `Agent decision`. */
function humanise(value: string): string {
    const words = value.replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

function truncate(value: string, max: number): string {
    return value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;
}

/**
 * `now` in IST, so "is this today?" is asked in the feed's own timezone rather
 * than the viewer's.
 */
function istNow(): moment.Moment {
    return moment().utcOffset(CONTROLX_TZ_OFFSET);
}

/** Does the string carry an explicit UTC offset (`Z`, `+05:30`, `-0400`)? */
function hasExplicitOffset(raw: string): boolean {
    return /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
}

/**
 * Parses an activity `timestamp` and pins it to IST for display.
 *
 * The spec claims these are "IST-naive datetimes (no timezone suffix)". The live
 * API disagrees — every row observed carries an explicit offset
 * (`2026-09-01T13:09:44+05:30`) — so both forms are handled: an explicit offset
 * is honoured, a naive string is read as IST per the spec. The result is then
 * rendered at +05:30, because `moment(offsetAware)` would otherwise convert to
 * the browser's zone and show a Delhi 13:09 as 07:39 to anyone outside India.
 */
function parseActivityTime(value: string | null | undefined): moment.Moment | null {
    if (!value || typeof value !== 'string') return null;
    const raw = value.trim();
    const parsed = moment.parseZone(hasExplicitOffset(raw) ? raw : `${raw}${CONTROLX_TZ_OFFSET}`);
    return parsed.isValid() ? parsed.utcOffset(CONTROLX_TZ_OFFSET) : null;
}

/**
 * Parses `watching.last_checked_at`, which is naive **UTC** — a different
 * convention from the activity timestamps sitting beside it in the same
 * response, so it cannot share their parser.
 *
 * Evidence, order 209175742: newest activity `13:09:44+05:30` (= 07:39:44 UTC)
 * and `last_checked_at: 2026-09-01T07:38:23`. That is one minute earlier read as
 * UTC, but five and a half hours earlier read as IST — nonsense for an order
 * ControlX had checked 134 times and had just seen delivered. Read as local it
 * rendered "07:38" where the truth is "13:08".
 */
function parseWatchingTime(value: string | null | undefined): moment.Moment | null {
    if (!value || typeof value !== 'string') return null;
    const raw = value.trim();
    const parsed = hasExplicitOffset(raw) ? moment.parseZone(raw) : moment.utc(raw);
    return parsed.isValid() ? parsed.utcOffset(CONTROLX_TZ_OFFSET) : null;
}

/**
 * Clock face for the rail: 24-hour with seconds, e.g. `13:21:53`.
 *
 * Not `constant.timeFormat` ('hh:mm A'), which is itself a hardcoded sibling
 * rather than an Account-Settings value: an agent feed can put several entries
 * in the same minute, so seconds are load-bearing here for reading order. The
 * date half still follows the centralised `constant.dateFormat`.
 */
const CONTROLX_CLOCK_FORMAT = 'HH:mm:ss';

/** `13:21:53` today, `01 Sep 2026 · 13:21:53` otherwise. */
function formatIstMoment(parsed: moment.Moment | null): string {
    if (!parsed) return '';
    return parsed.isSame(istNow(), 'day')
        ? parsed.format(CONTROLX_CLOCK_FORMAT)
        : parsed.format(`${constant.dateFormat} · ${CONTROLX_CLOCK_FORMAT}`);
}

/** An activity row's short time label, in IST. */
export function formatControlxTime(value: string | null | undefined): string {
    return formatIstMoment(parseActivityTime(value));
}

/** Always the full date+time, for tooltips where `14:22` alone is ambiguous. */
export function formatControlxTimeFull(value: string | null | undefined): string {
    const parsed = parseActivityTime(value);
    if (!parsed) return '';
    return `${parsed.format(`${constant.dateFormat} · ${constant.timeFormat}`)} IST`;
}

/** Flattens one value of unknown shape to a short, safe string. */
function scalarText(value: unknown, depth = 0): string {
    if (value === null || value === undefined) return '';
    const type = typeof value;
    if (type === 'string') return (value as string).trim();
    if (type === 'number' || type === 'boolean') return String(value);
    if (Array.isArray(value)) {
        if (depth > 1) return `${value.length} item${value.length === 1 ? '' : 's'}`;
        return value.map(item => scalarText(item, depth + 1)).filter(Boolean).join(', ');
    }
    if (type === 'object') {
        if (depth > 1) return '…';
        try {
            const json = JSON.stringify(value);
            return json && json !== '{}' ? json : '';
        } catch {
            // Circular or otherwise unserialisable — a feed row is not worth throwing over.
            return '';
        }
    }
    return '';
}

/** Keys that only repeat what the row already shows, or that are pure plumbing. */
const DETAIL_SKIP_KEYS = new Set([
    'id', 'activity_id', 'order_id', 'trip_id', 'tenant_user_id', 'branch_id',
    'timestamp', 'severity', 'icon', 'message', 'type'
]);

/**
 * Keys a producer uses when it has already written a human line, best first.
 *
 * `reason` outranks `body` because agent rows (SUPERVISOR `agent_decision`,
 * ALERT `support_escalation`) put their rationale there, which is the useful
 * half. `body` is next for COMMS rows, whose payload is the actual WhatsApp/push
 * text — without it those fell through to a key dump led by `Body: …`.
 */
const DETAIL_TEXT_KEYS = ['summary', 'description', 'reason', 'body', 'text', 'note', 'detail'];

/**
 * `detail` has no fixed schema — the spec says its shape varies by category —
 * so this handles string, scalar, array and object forms and gives up quietly
 * rather than rendering `[object Object]` or throwing on a shape nobody
 * anticipated.
 */
export function formatControlxDetail(detail: unknown, maxLength = 220): string {
    if (detail === null || detail === undefined) return '';

    if (typeof detail === 'string') return truncate(detail.trim(), maxLength);
    if (typeof detail === 'number' || typeof detail === 'boolean') return String(detail);
    if (Array.isArray(detail)) return truncate(scalarText(detail), maxLength);
    if (typeof detail !== 'object') return '';

    const record = detail as Record<string, unknown>;

    // Prefer a sentence the producer already wrote over a key/value dump.
    for (const key of DETAIL_TEXT_KEYS) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) return truncate(value.trim(), maxLength);
    }

    const parts: string[] = [];
    for (const key of Object.keys(record)) {
        if (DETAIL_SKIP_KEYS.has(key)) continue;
        const text = scalarText(record[key]);
        if (!text) continue;
        parts.push(`${humanise(key)}: ${text}`);
        if (parts.join(' · ').length >= maxLength) break;
    }
    return truncate(parts.join(' · '), maxLength);
}

/**
 * The fields that do not get their own line, gathered into the row tooltip so
 * nothing the API sends is dropped on the floor.
 */
function buildContextTitle(entry: ControlxActivity): string {
    const parts: string[] = [];
    const push = (label: string, value: unknown) => {
        const text = scalarText(value);
        if (text) parts.push(`${label}: ${text}`);
    };
    push('Agent', entry.agent_name);
    push('Feed', entry.prefix);
    push('Category', entry.category ? humanise(entry.category) : '');
    push('Severity', entry.severity ? humanise(String(entry.severity)) : '');
    push('Source', entry.source);
    push('Rider', entry.rider_id);
    push('Trip', entry.trip_id);
    push('Order', entry.order_id);
    push('Decision', entry.related_decision_id);
    push('Usage', entry.related_usage_id);
    return parts.join('\n');
}

/**
 * Card accent comes from severity, badge colour from prefix — deliberately two
 * different inputs. An SLA row is `severity: info` but must still read as SLA
 * amber, and an escalation is `severity: warning` on a red card; tying both to
 * one field cannot express that.
 */
function toneOf(severity: ControlxSeverity, prefix: string): ControlxTone {
    // Escalations are stamped `warning`, not `error` — the spec says as much
    // ("Cancels arrive as warning"), and the live feed confirms it: both
    // support_escalation rows on order 209175742 are severity `warning`. Left
    // alone they would read as amber advisories rather than the red they are.
    // Promotion is scoped to `warning` on purpose: an ALERT row stamped
    // `success` (a support ticket successfully raised) must stay green.
    if (severity === 'warning') {
        const feed = prefix.trim().toUpperCase();
        if (feed === 'ESCALATION' || feed === 'ALERT') return 'danger';
    }

    switch (severity) {
        case 'critical': return 'critical';
        case 'error': return 'danger';
        case 'warning': return 'warning';
        case 'success': return 'success';
        default: return 'info';
    }
}

/** Spoken/hover label for the accent, so tone is not colour-only. */
function toneLabelOf(tone: ControlxTone): string {
    switch (tone) {
        case 'critical': return 'Critical';
        case 'danger': return 'Needs attention';
        case 'warning': return 'Warning';
        case 'success': return 'Completed';
        default: return 'Information';
    }
}

function prefixToneOf(prefix: string): ControlxPrefixTone {
    switch (prefix.trim().toUpperCase()) {
        case 'SUPERVISOR':
        case 'AGENT': return 'agent';
        case 'SLA': return 'sla';
        case 'ESCALATION':
        case 'ALERT': return 'escalation';
        case 'COMMS': return 'comms';
        case 'RISK': return 'risk';
        case 'ORDER': return 'order';
        default: return 'default';
    }
}

/**
 * Categories where the agent committed to something rather than observed it.
 *
 * Deliberately narrow. `react_reasoning` is the category on SLA and RISK
 * "within range / not in scope" rows — that is the agent thinking out loud, not
 * deciding, and lumping it in here would make Decisions the whole feed.
 */
const DECISION_CATEGORIES = new Set(['agent_decision']);

/**
 * Feeds whose rows are decisions whatever else they carry.
 *
 * SUPERVISOR is the deciding agent — its badge is the one ops reads as "a call
 * was made here" — so the prefix outranks every other signal below, including
 * the reasoning exclusion. Note this is the raw `prefix` (the badge text), not
 * `prefixTone`, which folds SUPERVISOR and AGENT together for colour.
 */
const DECISION_PREFIXES = new Set(['SUPERVISOR']);

/**
 * Categories where ControlX reached someone outside itself — a support desk, a
 * partner's ticket queue, or the customer.
 */
const CONTACT_CATEGORIES = new Set([
    'support_message', 'support_escalation', 'swiggy_ticket'
]);

/** Producer-coined category suffixes that still mean "somebody was contacted". */
const CONTACT_SUFFIXES = /_(notify|notification|message|msg|ticket|escalation|alert|sms|email|call)$/;

/** The feeds that exist to carry outbound contact, used when `category` is absent. */
const CONTACT_PREFIXES = new Set(['COMMS', 'ESCALATION', 'ALERT', 'SUPPORT']);

/**
 * The category on "SLA within range" / "Event not in scope" rows — the agent
 * narrating a check it just ran. Never a decision, and never a contact even on
 * the COMMS feed, where it is precisely the row that says nobody was contacted.
 */
const REASONING_CATEGORY = 'react_reasoning';

/**
 * Did the agent commit to an outcome, rather than observe or narrate one?
 *
 * The SUPERVISOR badge decides it outright. Everything after that is a
 * fallback for rows on other feeds, read from several signals rather than a
 * category whitelist: `category` is optional in the wire contract ("payload
 * keys can vary slightly by producer") and the live feed leaves it null on some
 * rows — with a whitelist alone those all file silently under Actions and the
 * Decisions tab sits permanently empty, which is indistinguishable from a
 * broken filter.
 */
function isDecisionRow(
    entry: ControlxActivity, category: string, prefix: string, statusLabel: string
): boolean {
    if (DECISION_PREFIXES.has(prefix.trim().toUpperCase())) return true;

    const key = category.trim().toLowerCase();
    if (key === REASONING_CATEGORY) return false;
    if (DECISION_CATEGORIES.has(key) || key.endsWith('_decision')) return true;
    // The API stamps this only on a row that is, or resolves, a decision.
    if (scalarText(entry?.related_decision_id)) return true;
    // detail.trigger_type, e.g. SUPERVISOR_DECISION.
    if (statusLabel.includes('DECISION')) return true;
    return false;
}

/** Did this row reach a human or a partner desk? */
function isContactRow(entry: ControlxActivity, category: string, prefix: string): boolean {
    const key = category.trim().toLowerCase();
    // A COMMS row that concluded "no communication action needed" reached
    // nobody, so reasoning is excluded before the feed is even consulted.
    if (key === REASONING_CATEGORY) return false;
    if (key) {
        return CONTACT_CATEGORIES.has(key) || CONTACT_SUFFIXES.test(key);
    }
    // No category: fall back to the feed the producer filed it under, which is
    // what the row's own badge already shows the reader.
    return CONTACT_PREFIXES.has(prefix.trim().toUpperCase());
}

/** Node glyph: severity first (an alert always looks like one), then the API's own hint. */
function iconKeyOf(entry: ControlxActivity, tone: ControlxTone, prefix: string): ControlxIconKey {
    // Severity wins: an escalation must look like one whatever produced it.
    if (tone === 'critical' || tone === 'danger') return 'alert';

    // `category` is the most specific thing the API says about a row, so it is
    // read before the coarser `icon` hint and before the feed prefix.
    const category = typeof entry.category === 'string' ? entry.category.trim().toLowerCase() : '';
    switch (category) {
        case 'agent_decision': return 'brain';
        case 'tool_execution': return 'tool';
        case 'order_event': return 'package';
        case 'support_escalation': return 'alert';
        case 'swiggy_ticket': return 'ticket';
        case 'support_message': return 'comms';
        // `react_reasoning` deliberately falls through to the prefix switch: it
        // is the category on both SLA and RISK rows, and "which agent is
        // reasoning" is the useful distinction, not "this is reasoning".
    }
    if (category.endsWith('_notify')) return 'comms';

    const icon = typeof entry.icon === 'string' ? entry.icon.trim().toLowerCase() : '';
    if (icon === 'comms' || icon === 'message-circle') return 'comms';
    if (icon === 'ticket') return 'ticket';
    if (icon === 'alert') return 'alert';

    switch (prefix.trim().toUpperCase()) {
        case 'SLA': return 'clock';
        case 'RISK': return 'shield';
        case 'ORDER': return 'package';
        case 'COMMS': return 'comms';
    }
    return 'chip';
}

/**
 * `agent_name` as a readable role.
 *
 * The producers stamp a framework prefix or an `_agent` suffix onto the same
 * underlying role — `react_supervisor` and `supervisor_agent` are both the
 * supervisor — so those affixes are stripped before humanising, which is what
 * makes both render as "Supervisor" with the raw name still shown beneath.
 */
function agentDisplayNameOf(raw: string): string {
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed) return '';
    const core = trimmed
        .replace(/^(react|ctrlx|controlx)[_-]+/, '')
        .replace(/[_-]+(agent|bot|service|worker)$/, '');
    return humanise(core || trimmed);
}

function initialsOf(name: string): string {
    const words = name.split(/\s+/).filter(Boolean);
    if (!words.length) return '';
    const letters = words.length > 1
        ? `${words[0][0]}${words[1][0]}`
        : words[0].slice(0, 2);
    return letters.toUpperCase();
}

/** Radius 15.5 in a 36-unit box; kept here so the ring maths has one source. */
const CONF_RING_CIRCUMFERENCE = 2 * Math.PI * 15.5;

/**
 * `detail.confidence` as a whole percent.
 *
 * Accepts both conventions: the observed payloads use 0–1 (`0.9`), but a
 * producer sending `90` should not render as 9000%.
 */
function confidenceOf(detail: unknown): number | null {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
    const raw = (detail as Record<string, unknown>)['confidence'];
    const value = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(value) || value < 0) return null;
    const percent = value <= 1 ? value * 100 : value;
    return Math.min(100, Math.round(percent));
}

/** A short machine-ish status, e.g. `supervisor_decision` → SUPERVISOR_DECISION. */
function statusLabelOf(detail: unknown): string {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return '';
    const raw = (detail as Record<string, unknown>)['trigger_type'];
    return typeof raw === 'string' && raw.trim() ? raw.trim().toUpperCase() : '';
}

export function toControlxActivityView(entry: ControlxActivity): ControlxActivityView {
    const message = typeof entry?.message === 'string' ? entry.message.trim() : '';
    const category = typeof entry?.category === 'string' ? entry.category.trim() : '';
    const source = typeof entry?.source === 'string' ? entry.source.trim() : '';
    const prefix = typeof entry?.prefix === 'string' ? entry.prefix.trim() : '';
    const agentRaw = typeof entry?.agent_name === 'string' ? entry.agent_name.trim() : '';

    const severity = normaliseSeverity(entry?.severity);
    const tone = toneOf(severity, prefix);
    const agentDisplayName = agentDisplayNameOf(agentRaw);
    const confidence = confidenceOf(entry?.detail);
    const statusLabel = statusLabelOf(entry?.detail);

    return {
        id: controlxEntryId(entry),
        severity,
        // A row with no message would otherwise render as an empty bullet.
        message: message || humanise(category) || 'ControlX activity',
        detailText: formatControlxDetail(entry?.detail),
        timeLabel: formatControlxTime(entry?.timestamp),
        timeTitle: formatControlxTimeFull(entry?.timestamp),
        contextTitle: buildContextTitle(entry),
        category,
        source,

        prefixLabel: prefix.toUpperCase(),
        prefixTone: prefixToneOf(prefix),
        tone,
        toneLabel: toneLabelOf(tone),
        // Only escalation cards carry the origin badge. On a routine row it is
        // noise — every entry in an adloggs tenant says "adloggs" — but on an
        // escalation, which side raised it is the first thing ops asks.
        sourceLabel: (tone === 'danger' || tone === 'critical') ? source.toUpperCase() : '',
        agentDisplayName,
        agentRaw,
        agentInitials: initialsOf(agentDisplayName),
        confidence,
        confidenceDash: confidence === null
            ? ''
            : `${(CONF_RING_CIRCUMFERENCE * confidence / 100).toFixed(2)} ${CONF_RING_CIRCUMFERENCE.toFixed(2)}`,
        statusLabel,
        iconKey: iconKeyOf(entry, tone, prefix),
        group: isDecisionRow(entry, category, prefix, statusLabel) ? 'decision' : 'action',
        isContact: isContactRow(entry, category, prefix),

        entry
    };
}

/**
 * Caption for the "nothing has happened yet" state.
 *
 * An empty timeline on a healthy order is the expected outcome, not a failure,
 * so this reports what ControlX last did rather than implying it is not looking.
 */
export function formatControlxWatching(watching: ControlxWatching | null | undefined): string {
    if (!watching) return '';
    // Its own parser — this field is naive UTC, the activity rows are not.
    const checked = formatIstMoment(parseWatchingTime(watching.last_checked_at));
    if (checked) return `Watching · last checked ${checked}`;
    const checks = Number(watching.checks ?? 0);
    if (checks > 0) return `Watching · ${checks} check${checks === 1 ? '' : 's'} so far`;
    return 'Watching this order';
}
